/**
 * IP address validation
 *
 * Used to validate A / AAAA record content before submitting it to the DNS API.
 * Hand-rolled single-regex IPv6 checks cannot express the ":: appears at most
 * once" rule, so the address is parsed structurally instead.
 */

const IPV4_OCTET = /^(0|[1-9]\d{0,2})$/
const IPV6_GROUP = /^[0-9a-fA-F]{1,4}$/

/**
 * Dotted-quad IPv4 address, e.g. "148.251.201.92".
 * Leading zeros are rejected: they are parsed as octal by some resolvers.
 */
export function isValidIpv4(value: string): boolean {
  const parts = value.split('.')
  if (parts.length !== 4) return false
  return parts.every((part) => IPV4_OCTET.test(part) && Number(part) <= 255)
}

/**
 * Count the 16-bit groups in one half of an IPv6 address.
 * Returns null when any group is malformed.
 *
 * @param groups - colon-separated groups
 * @param allowIpv4Tail - whether the final group may be a dotted-quad (e.g. "::ffff:192.0.2.1")
 */
function countIpv6Groups(groups: string[], allowIpv4Tail: boolean): number | null {
  let count = 0

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i]
    const isLast = i === groups.length - 1

    if (isLast && allowIpv4Tail && group.includes('.')) {
      if (!isValidIpv4(group)) return null
      count += 2
      continue
    }

    if (!IPV6_GROUP.test(group)) return null
    count += 1
  }

  return count
}

/**
 * IPv6 address, including "::" compression and IPv4-mapped forms.
 * Zone identifiers ("fe80::1%eth0") are rejected: they are not valid record content.
 */
export function isValidIpv6(value: string): boolean {
  if (!value || value.includes('%')) return false

  const compressionCount = value.split('::').length - 1
  if (compressionCount > 1) return false

  if (compressionCount === 0) {
    const groups = value.split(':')
    return countIpv6Groups(groups, true) === 8
  }

  const [head, tail] = value.split('::')
  const headCount = head === '' ? 0 : countIpv6Groups(head.split(':'), false)
  const tailCount = tail === '' ? 0 : countIpv6Groups(tail.split(':'), true)
  if (headCount === null || tailCount === null) return false

  // "::" stands for at least one group of zeros
  return headCount + tailCount <= 7
}

/** Either an IPv4 or an IPv6 address, as the two checks above define them. */
export function isValidIpAddress(value: string): boolean {
  return isValidIpv4(value) || isValidIpv6(value)
}
